import { Control, Controller, useWatch } from "react-hook-form";

import {
  DateInput,
  ErrorMessage,
  Field,
  GoogleMapAutoComplete,
  Input,
  InputWithAddressAutoComplete,
  Label,
  PhoneInput,
  TextArea,
  validatePhone,
} from "@web-app/components";
import { GoogleAddress, OrderDeliveryType, SavedAddress } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import { errorMessages, getIsRequired } from "@web-app/utils/order";

import { FormValues } from "../../types";

interface DropoffDetailsTabProps {
  control: Control<FormValues>;
  currentWorkspaceId: string;
  dropoffLocation: GoogleAddress | null;
  handleAddressSelect: (address: SavedAddress, isPickup: boolean) => void;
  isIndividualDelivery: boolean;
  onAddressSelect: (address: GoogleAddress | null, isPickup: boolean) => void;
  onSaveDropoffDetailsChange: (value: boolean) => void;
  saveDropoffDetails: boolean;
}

export function DropoffDetailsTab({
  control,
  currentWorkspaceId,
  dropoffLocation,
  handleAddressSelect,
  isIndividualDelivery,
  onAddressSelect,
  onSaveDropoffDetailsChange,
  saveDropoffDetails,
}: DropoffDetailsTabProps) {
  const timezone = useWatch({
    control,
    name: "timezone",
  }) as keyof typeof timeZoneMap | undefined;

  return (
    <div className="space-y-3 md:space-y-4 pb-10 md:pb-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label required>Full Name</Label>
          <Controller
            name="dropOffName"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                propertyName: "dropOffName",
              })
                ? errorMessages.dropoff.dropOffName
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <InputWithAddressAutoComplete
                  type="text"
                  placeholder="Enter full name"
                  currentWorkspaceId={currentWorkspaceId}
                  onClickAddress={(address: { name: string }) => {
                    field.onChange(address.name);
                  }}
                  onAddressSelect={async (address) => {
                    await handleAddressSelect(address, false);
                  }}
                  {...field}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        <Field>
          <Label required>Phone Number</Label>
          <Controller
            name="dropOffPhone"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                propertyName: "dropOffPhone",
              })
                ? errorMessages.dropoff.dropOffPhone
                : false,
              validate: (value: string) => {
                if (!validatePhone(value)) {
                  return "Please enter a valid phone number (e.g. +1234567890)";
                }
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <PhoneInput
                  {...field}
                  onChange={(value: string) => {
                    field.onChange(value);
                  }}
                  defaultValue={field.value ? field.value : ""}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
      <Field>
        <Label required>Address</Label>
        <Controller
          name="dropoffLocation"
          control={control}
          rules={{
            required: getIsRequired({
              orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
              propertyName: "dropoffLocation",
            })
              ? errorMessages.dropoff.dropoffLocation
              : false,
          }}
          render={({ fieldState: { error }, field: { onChange } }) => (
            <>
              <GoogleMapAutoComplete
                address={dropoffLocation}
                setAddress={(address) => {
                  onAddressSelect(address, false);
                  onChange(address);
                }}
                placeholder="Enter drop-off location"
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </>
          )}
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isIndividualDelivery && (
          <Field>
            <Label required>Drop-off After (Date/Time)</Label>
            <Controller
              name="dropOffStartTime"
              control={control}
              rules={{
                required: getIsRequired({
                  orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                  propertyName: "dropOffStartTime",
                })
                  ? "Start time is required"
                  : false,
              }}
              render={({
                field: { onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <>
                  <DateInput
                    minDate={new Date()}
                    value={value instanceof Date ? value : new Date()}
                    onChange={onChange}
                    {...rest}
                    invalid={!!error}
                    timezone={timezone}
                  />
                  {error && <ErrorMessage message={error.message} />}
                </>
              )}
            />
          </Field>
        )}
        <Field>
          <Label required>Drop-off At (End Time)</Label>
          <Controller
            name="scheduledEndDateTime"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                propertyName: "scheduledEndDateTime",
              })
                ? "End time is required"
                : false,
            }}
            render={({
              field: { onChange, ...rest },
              fieldState: { error },
            }) => (
              <>
                <DateInput
                  minDate={new Date()}
                  onChange={(date) => {
                    onChange(date);
                  }}
                  {...rest}
                  invalid={!!error}
                  timezone={timezone}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field>
          <Label>Business Name</Label>
          <Controller
            name="dropOffBusinessName"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                propertyName: "dropOffBusinessName",
              })
                ? "Business name is required"
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  type="text"
                  placeholder="Enter business name"
                  {...field}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
        <Field>
          <Label>Service Time (minutes)</Label>
          <Controller
            name="dropOffServiceTime"
            control={control}
            rules={{
              min: {
                message: errorMessages.dropoff.dropOffServiceTime,
                value: 0,
              },
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_DROPOFF,
                propertyName: "dropOffServiceTime",
              })
                ? errorMessages.dropoff.dropOffServiceTime
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  type="number"
                  placeholder="Enter service time"
                  {...field}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </>
            )}
          />
        </Field>
      </div>
      <div className="space-y-2">
        <Label>Order Requirements</Label>
        <div className="flex flex-col sm:flex-row flex-wrap gap-x-4 gap-y-1 md:gap-y-2">
          <Controller
            name="dropoffOrderRequirements_SIGNATURE"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Signature Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="dropoffOrderRequirements_PHOTO"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Photo Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="dropoffOrderRequirements_NOTES"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Notes Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
          <Controller
            name="dropoffOrderRequirements_ITEMCHECKLIST"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Item Checklist Required"
                value={field.value.toString()}
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
              />
            )}
          />
        </div>
      </div>
      <Field>
        <Label>Note</Label>
        <Controller
          name="dropOffNote"
          control={control}
          render={({ field }) => (
            <TextArea placeholder="Enter drop-off note" rows={3} {...field} />
          )}
        />
      </Field>
      <Field>
        <Controller
          name="saveDropoffDetails"
          control={control}
          render={() => (
            <Input
              type="checkbox"
              rightLabel="Save details to Address Book"
              value={saveDropoffDetails.toString()}
              onChange={(value: string) => {
                const boolValue = value === "true";
                onSaveDropoffDetailsChange(boolValue);
              }}
            />
          )}
        />
      </Field>
    </div>
  );
}
